/**
 * Feb 07, 2026 — P0 Revenue Task
 * Admin Conversion Audit Dashboard
 * ---------------------------------------------------------------------
 * Route: /admin/conversion-audit
 * Access: admin only (get_current_user role check on the API)
 *
 * Renders 5 sections:
 *   1. Funnel — 8-stage drop-off table with %
 *   2. Top 5 Bottlenecks — auto-detected + suggested fixes
 *   3. Form Friction — per-field abandon rate
 *   4. Device Split — mobile vs desktop conversion
 *   5. Payment Split — pre vs post payment failures
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, RefreshCw, TrendingDown, AlertCircle, Smartphone, Monitor,
  CreditCard, Users, Target, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function ConversionAuditDashboard() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);

  const load = async (d = days) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(
        `${API}/api/admin/poocho-conversion-audit?days=${d}`,
        { withCredentials: true }
      );
      setReport(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(30); }, []);

  const totalLanded = report?.funnel?.[0]?.count || 0;
  const totalPaid = report?.funnel?.find(f => f.step === 'payment_success')?.count || 0;
  const overallCvr = totalLanded ? ((totalPaid / totalLanded) * 100).toFixed(2) : '0.00';

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 pb-20" data-testid="conversion-audit-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-950 via-slate-900 to-purple-950 border-b border-purple-500/30">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <Link to="/admin" className="inline-flex items-center gap-2 text-purple-300 hover:text-purple-200 text-sm mb-3">
            <ArrowLeft className="w-4 h-4" /> Back to Admin
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h1 className="text-3xl font-black bg-gradient-to-r from-amber-300 to-purple-300 bg-clip-text text-transparent">
                🎯 Poocho Guruji — Conversion Audit
              </h1>
              <p className="text-slate-400 text-sm mt-1">
                Funnel drop-off, form friction, device split & auto-detected fixes.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={days}
                onChange={(e) => { setDays(parseInt(e.target.value, 10)); load(parseInt(e.target.value, 10)); }}
                className="bg-slate-800 border border-purple-500/30 rounded px-3 py-2 text-sm"
                data-testid="audit-range-select"
              >
                <option value={7}>Last 7 days</option>
                <option value={30}>Last 30 days</option>
                <option value={90}>Last 90 days</option>
                <option value={365}>Last 365 days</option>
              </select>
              <button
                onClick={() => load(days)}
                disabled={loading}
                className="px-3 py-2 rounded bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold text-sm flex items-center gap-1 disabled:opacity-50"
                data-testid="audit-refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6 space-y-6">
        {error && (
          <div className="bg-red-950 border border-red-500 rounded p-4 flex items-center gap-2" data-testid="audit-error">
            <AlertCircle className="w-5 h-5 text-red-400" /> {error}
          </div>
        )}
        {!report && loading && (
          <div className="text-center py-20 text-slate-400">Loading conversion audit…</div>
        )}

        {report && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <SummaryCard icon={<Users className="w-5 h-5" />} label="Total Landed"    value={totalLanded} color="text-blue-300" testid="stat-landed" />
              <SummaryCard icon={<CreditCard className="w-5 h-5" />} label="Paid Sessions"  value={totalPaid}  color="text-emerald-300" testid="stat-paid" />
              <SummaryCard icon={<Target className="w-5 h-5" />} label="Overall CVR"      value={`${overallCvr}%`} color="text-amber-300" testid="stat-cvr" />
              <SummaryCard icon={<TrendingDown className="w-5 h-5" />} label="Top 5 Bottlenecks" value={report.top_5_bottlenecks?.length || 0} color="text-red-300" testid="stat-bottlenecks" />
            </div>

            {/* 1. Funnel Table */}
            <Section title="📊 Funnel — 8 stages" testid="section-funnel">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left">Stage</th>
                      <th className="px-3 py-2 text-right">Count</th>
                      <th className="px-3 py-2 text-right">% of top</th>
                      <th className="px-3 py-2 text-right">Drop from prev</th>
                      <th className="px-3 py-2 text-right">Drop %</th>
                      <th className="px-3 py-2 w-1/3">Visual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.funnel.map(row => (
                      <tr key={row.step} className="border-t border-slate-800" data-testid={`funnel-row-${row.step}`}>
                        <td className="px-3 py-3 font-bold text-slate-100">{row.label}</td>
                        <td className="px-3 py-3 text-right font-mono text-amber-300">{row.count.toLocaleString()}</td>
                        <td className="px-3 py-3 text-right font-mono text-slate-300">{row.pct_of_top.toFixed(1)}%</td>
                        <td className="px-3 py-3 text-right font-mono text-red-300">
                          {row.drop_from_prev === null ? '—' : row.drop_from_prev.toLocaleString()}
                        </td>
                        <td className="px-3 py-3 text-right font-mono text-red-400">
                          {row.drop_pct === null ? '—' : `${row.drop_pct.toFixed(1)}%`}
                        </td>
                        <td className="px-3 py-3">
                          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-amber-500 to-purple-500"
                              style={{ width: `${row.pct_of_top}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Section>

            {/* 2. Top 5 Bottlenecks + Fixes */}
            <Section title="🔴 Top 5 Bottlenecks + Recommended Fixes" testid="section-bottlenecks">
              {(!report.top_5_bottlenecks || report.top_5_bottlenecks.length === 0) ? (
                <p className="text-slate-500 text-sm">✅ कोई critical bottleneck नहीं मिली — funnel healthy दिख रहा है।</p>
              ) : (
                <div className="space-y-4">
                  {report.top_5_bottlenecks.map((b, i) => (
                    <div key={b.code} className="bg-slate-900 border border-red-900/50 rounded-lg p-4" data-testid={`bottleneck-${i + 1}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="w-7 h-7 rounded-full bg-red-600 text-white font-black flex items-center justify-center text-sm">{i + 1}</span>
                          <h3 className="font-bold text-slate-100">{b.label}</h3>
                          <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400 capitalize">{b.category.replace('_', ' ')}</span>
                        </div>
                        <div className="text-right">
                          <div className="text-red-400 font-mono text-sm">-{b.impact} users</div>
                          <div className="text-red-300 font-mono text-xs">{b.drop_pct?.toFixed(1)}% drop</div>
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3 mt-3">
                        <div className="bg-red-950/30 border border-red-800/50 rounded p-3">
                          <p className="text-[10px] uppercase tracking-wider text-red-300 mb-1 font-bold">Problem</p>
                          <p className="text-sm text-slate-200">{b.problem}</p>
                        </div>
                        <div className="bg-emerald-950/30 border border-emerald-800/50 rounded p-3">
                          <p className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1 font-bold">✅ Suggested Fix</p>
                          <p className="text-sm text-slate-200">{b.suggested_fix}</p>
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 mt-2">{b.detail}</p>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* 3. Form Friction */}
            <Section title="📝 Form Field Friction — per-field abandon rate" testid="section-friction">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wide">
                    <tr>
                      <th className="px-3 py-2 text-left">Field</th>
                      <th className="px-3 py-2 text-right">Touched sessions</th>
                      <th className="px-3 py-2 text-right">Abandons at field</th>
                      <th className="px-3 py-2 text-right">Abandon %</th>
                      <th className="px-3 py-2 w-1/3">Visual</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.form_friction.map(row => (
                      <tr key={row.field} className="border-t border-slate-800" data-testid={`friction-row-${row.field}`}>
                        <td className="px-3 py-3 font-mono text-amber-300">{row.field}</td>
                        <td className="px-3 py-3 text-right font-mono text-slate-300">{row.touched_sessions}</td>
                        <td className="px-3 py-3 text-right font-mono text-red-400">{row.abandons_at_field}</td>
                        <td className="px-3 py-3 text-right font-mono text-red-300">{row.abandon_pct.toFixed(1)}%</td>
                        <td className="px-3 py-3">
                          <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-red-500 to-amber-500"
                              style={{ width: `${Math.min(row.abandon_pct, 100)}%` }}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {report.form_friction.every(r => r.touched_sessions === 0) && (
                <p className="text-slate-500 text-xs mt-3 italic">
                  ⏳ अभी field-level beacons collect होना शुरू हुए हैं। 24 hours में यह table meaningful data दिखाएगा।
                </p>
              )}
            </Section>

            {/* 4. Device Split */}
            <Section title="📱 Device Split — Mobile vs Desktop" testid="section-device">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {report.device_split.map(d => (
                  <div key={d.device} className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={`device-${d.device}`}>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        {d.device === 'mobile' ? <Smartphone className="w-4 h-4 text-blue-400" /> : <Monitor className="w-4 h-4 text-purple-400" />}
                        <h4 className="font-bold text-slate-100 capitalize">{d.device}</h4>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded bg-slate-800 text-slate-400">{d.landings} landings</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <Row label="Form Submits"        value={d.form_submits} />
                      <Row label="Paid"                value={d.paid} strong />
                      <Row label="Land→Submit"         value={`${d.landing_to_submit_pct.toFixed(1)}%`} color="text-blue-300" />
                      <Row label="Submit→Paid"         value={`${d.submit_to_paid_pct.toFixed(1)}%`}   color="text-blue-300" />
                      <Row label="Overall CVR"         value={`${d.overall_conversion_pct.toFixed(2)}%`} color={d.overall_conversion_pct > 3 ? 'text-emerald-300' : 'text-red-300'} strong />
                    </div>
                  </div>
                ))}
              </div>
            </Section>

            {/* 5. Payment Split */}
            <Section title="💳 Payment Split — Pre vs Post" testid="section-payment">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <StatBox label="Total Sessions"          value={report.payment_split.total_sessions_created}  color="text-slate-100" testid="pay-total" />
                <StatBox label="Never Started Payment"   value={report.payment_split.never_started_payment} color="text-amber-300" testid="pay-never" />
                <StatBox label="Pre-Payment Drops"       value={report.payment_split.pre_payment_drops}      color="text-red-300"    testid="pay-pre-drop" />
                <StatBox label="Post-Payment Failures"   value={report.payment_split.post_payment_failures}  color="text-red-400"    testid="pay-post-fail" />
                <StatBox label="Successful Payments"     value={report.payment_split.successful_payments}    color="text-emerald-300" testid="pay-success" />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
                <div className="bg-slate-900 rounded p-3">
                  <p className="text-xs text-slate-400 mb-1">Pre-payment drop %</p>
                  <p className="text-2xl font-mono text-red-300">{report.payment_split.pre_payment_drop_pct.toFixed(1)}%</p>
                </div>
                <div className="bg-slate-900 rounded p-3">
                  <p className="text-xs text-slate-400 mb-1">Post-payment failure %</p>
                  <p className="text-2xl font-mono text-red-300">{report.payment_split.post_payment_fail_pct.toFixed(1)}%</p>
                </div>
              </div>
            </Section>

            <p className="text-xs text-slate-500 text-center pt-4">
              📈 Fine-grained field-level data grows richer as more users interact. Beacons live since Feb 07, 2026.
              <br />Report generated: {new Date(report.generated_at).toLocaleString()}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────

const SummaryCard = ({ icon, label, value, color, testid }) => (
  <div className="bg-slate-900 border border-slate-800 rounded-lg p-4" data-testid={testid}>
    <div className="flex items-center justify-between mb-2">
      <div className={color}>{icon}</div>
    </div>
    <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
    <div className={`text-2xl font-black mt-1 ${color}`}>{value}</div>
  </div>
);

const Section = ({ title, children, testid }) => (
  <div className="bg-slate-900/60 backdrop-blur border border-slate-800 rounded-xl overflow-hidden" data-testid={testid}>
    <div className="bg-slate-900 px-4 py-3 border-b border-slate-800">
      <h2 className="text-lg font-bold text-slate-100 flex items-center gap-2">
        <Zap className="w-4 h-4 text-amber-400" /> {title}
      </h2>
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Row = ({ label, value, color = 'text-slate-200', strong }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-400 text-xs">{label}</span>
    <span className={`${color} ${strong ? 'font-black text-base' : 'font-mono text-sm'}`}>{value}</span>
  </div>
);

const StatBox = ({ label, value, color, testid }) => (
  <div className="bg-slate-900 border border-slate-800 rounded p-3 text-center" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">{label}</div>
    <div className={`text-2xl font-black ${color}`}>{value}</div>
  </div>
);
